import { createApiClient } from '../../shared/http/createApiClient.js';

import { API_BASE as BASE } from '../../shared/http/apiBase.js';

const client = createApiClient({ auth: true });
const req      = (method, path, body) => client.request(path, { method, body });
const download = (path) => client.download(path);

const taxonomySaves = new Map();
const taxonomySaveErrors = new Map();
function trackTaxonomySave(key, promise) {
  const pending = taxonomySaves.get(key) || new Set();
  taxonomySaves.set(key, pending); pending.add(promise);
  promise.then(() => taxonomySaveErrors.delete(key), e => taxonomySaveErrors.set(key, e))
    .finally(() => pending.delete(promise));
  return promise;
}

export const txApi = {
  waitForTaxonomySaves: async (gid, pid) => {
    const keys = [`${gid}:${pid}`, `${gid}:*`];
    let pending;
    while ((pending = keys.flatMap(k => [...(taxonomySaves.get(k) || [])])).length) await Promise.all(pending);
    for (const key of keys) if (taxonomySaveErrors.has(key)) throw taxonomySaveErrors.get(key);
  },
  // UTM is a separate configuration and persistence surface.
  getUtmSetup: (cid, vid) => req('GET', `/tx/clients/${cid}/utm-setup${vid ? `?version_id=${vid}` : ''}`),
  saveUtmSetup: (cid, body) => req('PUT', `/tx/clients/${cid}/utm-setup`, body),
  getUtmFormulaFunctions: cid => req('GET', `/tx/clients/${cid}/utm-formula`),
  testUtmFormula: (cid, body) => req('POST', `/tx/clients/${cid}/utm-formula`, body),
  getUtmRows: (gid, pid) => req('GET', `/tx/campaign_groups/${gid}/utm?platform_id=${pid}`),
  saveUtmRows: (gid, pid, body) => req('PUT', `/tx/campaign_groups/${gid}/utm?platform_id=${pid}`, body),
  getUtmHistory: (gid, pid) => req('GET', `/tx/campaign_groups/${gid}/utm/history${pid ? `?platform_id=${pid}` : ''}`),
  getUtmVersion: (gid, vid) => req('GET', `/tx/campaign_groups/${gid}/utm/history/${vid}`),
  restoreUtmVersion: (gid, vid, body) => req('POST', `/tx/campaign_groups/${gid}/utm/history/${vid}`, body),
  duplicateUtm: (gid, sourceId) => req('POST', `/tx/campaign_groups/${gid}/utm/duplicate`, { source_group_id: sourceId }),
  getUtmDuplicate: gid => req('GET', `/tx/campaign_groups/${gid}/utm/duplicate-preview`),
  // me / role
  getMe: () => req("GET", "/tx/me"),

  // platforms
  listPlatforms:    ()          => req("GET",   "/tx/platforms"),
  createPlatform:   (body)      => req("POST",  "/tx/platforms", body),
  updatePlatform:   (id, body)  => req("PATCH", `/tx/platforms/${id}`, body),

  // clients — created in Client Management, not here. This list is the clients enabled for
  // the Taxonomy Generator; the PATCH covers status and platform assignment only.
  listClients:      ()          => req("GET",   "/tx/clients"),
  updateClient:     (id, body)  => req("PATCH", `/tx/clients/${id}`, body),
  addClientPlatform: (id, body)  => req("POST",  `/tx/clients/${id}/platforms`, body),
  removeClientPlatform: (id, pid) => req("DELETE", `/tx/clients/${id}/platforms/${pid}`),

  // users
  listUsers:        ()          => req("GET",    "/tx/users"),
  grantUser:        (body)      => req("POST",   "/tx/users", body),
  createUser:       (body)      => req("POST",   "/tx/users/create", body),
  updateUser:       (uid, body) => req("PATCH",  `/tx/users/${uid}`, body),
  revokeUser:       (uid)       => req("DELETE", `/tx/users/${uid}`),

  // platform levels
  getPlatformLevels:   (pid)           => req("GET",    `/tx/platforms/${pid}/levels`),
  createPlatformLevel: (pid, body)     => req("POST",   `/tx/platforms/${pid}/levels`, body),
  updatePlatformLevel: (lid, body)     => req("PATCH",  `/tx/platform_levels/${lid}`, body),
  deletePlatformLevel: (lid)           => req("DELETE", `/tx/platform_levels/${lid}`),
  // What deleting a level would touch — taxonomies, fields, campaigns. Read-only.
  platformLevelUsage:  (lid)           => req("GET",    `/tx/platform_levels/${lid}/usage`),
  // What disabling a platform would touch. Pass clientId for the per-client switch on the
  // Clients tab; omit it for the global Deactivate on the Platforms tab. Read-only.
  platformUsage:       (pid, clientId) => req("GET",    `/tx/platforms/${pid}/usage${clientId ? `?client_id=${clientId}` : ''}`),

  // taxonomy structure
  // versionId reads that version's level row instead of the active one — archived versions
  // are viewable, so the editor below the version bar has to follow the bar's selection.
  getTaxonomy:      (cid, pid, level, versionId) => {
    const qs = new URLSearchParams();
    if (level) qs.set("level", level);
    if (versionId) qs.set("version_id", versionId);
    const q = qs.toString();
    return req("GET", `/tx/clients/${cid}/platforms/${pid}/taxonomy${q ? `?${q}` : ""}`);
  },
  upsertTaxonomy:   (cid, pid, body)        => req("PUT", `/tx/clients/${cid}/platforms/${pid}/taxonomy`, body),

  // taxonomy versions — scoped to client + platform, one active at a time.
  listTaxonomyVersions: (cid, pid) =>
    req("GET", `/tx/taxonomy-versions?client_id=${cid}&platform_id=${pid}`),
  getVersionFields:     (vid)       => req("GET",  `/tx/taxonomy-versions/${vid}/fields`),
  createTaxonomyVersion: (body)     => req("POST", "/tx/taxonomy-versions", body),
  restoreTaxonomyVersion: (vid)     => req("POST", `/tx/taxonomy-versions/${vid}/restore`),
  // What an in-place destructive edit would cost. Writes nothing.
  versionImpact:        (vid, fieldIds) =>
    req("POST", `/tx/taxonomy-versions/${vid}/impact`, { field_ids: fieldIds }),
  // Commits an in-place edit against the revision that was loaded. 409 when stale.
  touchVersion:         (vid, expectedRevision) =>
    req("POST", `/tx/taxonomy-versions/${vid}/touch`, { expected_revision: expectedRevision }),

  // Duplicate as migration — preview is read-only, duplicate commits a draft.
  duplicatePreview: (cid, targetVersionId) =>
    req("GET", `/tx/campaigns/${cid}/duplicate-preview${targetVersionId ? `?target_version_id=${targetVersionId}` : ""}`),
  duplicateCampaign: (cid, body) => req("POST", `/tx/campaigns/${cid}/duplicate`, body || {}),
  // Whole-group duplicate: the mapping only, since the copy is assembled in the builder and
  // is not written until the user saves it.
  groupDuplicatePreview: (gid, platformId) =>
    req("GET", `/tx/campaign_groups/${gid}/duplicate-preview${platformId ? `?platform_id=${platformId}` : ""}`),
  copyLevel:        (body)                  => req("POST", "/tx/copy_level", body),
  copyPlatform:     (body)                  => req("POST", "/tx/copy_platform", body),

  // fields
  createField:      (taxId, body)   => req("POST",   `/tx/taxonomy/${taxId}/fields`, body),
  updateField:      (fid, body)     => req("PATCH",  `/tx/fields/${fid}`, body),
  deleteField:      (fid)           => req("DELETE", `/tx/fields/${fid}`),

  // field list as a spreadsheet — one level per file
  exportFields: (params) =>
    download(`/tx/taxonomy_fields/export?${new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString()}`),
  importFields: (file, params) => {
    // Raw-file upload with special 422 (validation) handling — bypasses the shared
    // JSON client since the body is a binary blob, not JSON.
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return fetch(`${BASE}/tx/taxonomy_fields/import?${qs}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('mp_token') || ''}` },
      body: file,
    }).then(async res => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 422) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    });
  },

  // per-field dictionary (legacy / custom values)
  createDictEntry:  (fid, body)  => req("POST",   `/tx/fields/${fid}/dictionary`, body),
  updateDictEntry:  (eid, body)  => req("PATCH",  `/tx/dictionary/${eid}`, body),
  deleteDictEntry:  (eid)        => req("DELETE", `/tx/dictionary/${eid}`),

  // shared data dictionary lists
  listDictLists:      (clientId)   => req("GET",    `/tx/dict_lists${clientId ? `?client_id=${clientId}` : ''}`),
  createDictList:     (body)       => req("POST",   "/tx/dict_lists", body),
  updateDictList:     (id, body)   => req("PATCH",  `/tx/dict_lists/${id}`, body),
  deleteDictList:     (id)         => req("DELETE", `/tx/dict_lists/${id}`),
  getDictValues:      (id)         => req("GET",    `/tx/dict_lists/${id}/values`),
  addDictValues:      (id, body)   => req("POST",   `/tx/dict_lists/${id}/values`, body),
  updateDictValue:    (id, body)   => req("PATCH",  `/tx/dict_values/${id}`, body),
  deleteDictValue:    (id)         => req("DELETE", `/tx/dict_values/${id}`),
  excludeDictValue:   (lid, vid)   => req("POST",   `/tx/dict_lists/${lid}/exclude/${vid}`),
  unexcludeDictValue: (lid, vid)   => req("DELETE", `/tx/dict_lists/${lid}/exclude/${vid}`),
  exportAllDictLists: (clientId, clientName) => download(`/tx/dict_lists/export_all?client_id=${clientId || ''}&client_name=${encodeURIComponent(clientName || 'Global')}`),
  importAllDictLists: (file, clientId) => {
    // Raw-file upload with special 422 (validation) handling — bypasses the shared
    // JSON client since the body is a binary blob, not JSON.
    const params = clientId ? `?client_id=${clientId}` : '';
    return fetch(`${BASE}/tx/dict_lists/import_all${params}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('mp_token') || ''}` },
      body: file,
    }).then(async res => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok && res.status !== 422) throw new Error(data.error || `HTTP ${res.status}`);
      return data;
    });
  },

  // campaign groups (one per named campaign)
  listCampaignGroups:  (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return req("GET", `/tx/campaign_groups${qs ? "?" + qs : ""}`);
  },
  listDeletedCampaignGroups: (params = {}) => {
    const qs = new URLSearchParams({ ...Object.fromEntries(Object.entries(params).filter(([, v]) => v)), deleted: '1' }).toString();
    return req("GET", `/tx/campaign_groups?${qs}`);
  },
  checkCampaignGroupName: (name, clientId) => req("GET", `/tx/campaign_groups/check_name?name=${encodeURIComponent(name)}&client_id=${clientId}`),
  createCampaignGroup: (body) => req("POST", "/tx/campaign_groups", body),
  // A campaign that exists only so its rows can be saved: the server names it and flags it a
  // draft, and naming it later is an ordinary rename.
  createDraftCampaignGroup: (body) => req("POST", "/tx/campaign_groups", { ...body, draft: true }),
  getCampaignGroup:    (id)   => req("GET",  `/tx/campaign_groups/${id}`),
  // The builder's read: only the columns the grid renders, only the channel on screen, and
  // gzipped. A 16,000-row campaign is 18.9MB the other way.
  getCampaignGroupRows: (id, platformId) =>
    req("GET", `/tx/campaign_groups/${id}?slim=1${platformId ? `&platform_id=${platformId}` : ""}`),
  updateCampaignGroup: (id, body) => trackTaxonomySave(`${id}:*`, req("PATCH", `/tx/campaign_groups/${id}`, body)),
  deleteCampaignGroup: (id)   => req("DELETE", `/tx/campaign_groups/${id}`),
  restoreCampaignGroup: (id)  => req("POST", `/tx/campaign_groups/${id}/restore`),
  clearGroupRows:      (id, level) => req("DELETE", `/tx/campaign_groups/${id}/campaigns${level ? `?level=${encodeURIComponent(level)}` : ""}`),
  saveGroupLevel:      (id, level, platform_id, rows) => trackTaxonomySave(`${id}:${platform_id}`, req("POST", `/tx/campaign_groups/${id}/save_level`, { level, platform_id, rows })),
  getGroupVersions:    (id, level) => req("GET", `/tx/campaign_groups/${id}/versions${level ? `?level=${encodeURIComponent(level)}` : ''}`),
  getGroupVersion:     (id, vid)   => req("GET", `/tx/campaign_groups/${id}/versions/${vid}`),
  restoreGroupVersion: (id, vid)   => req("POST", `/tx/campaign_groups/${id}/versions/${vid}/restore`),

  // campaign rows (one per level per group)
  listCampaigns:    (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return req("GET", `/tx/campaigns${qs ? "?" + qs : ""}`);
  },
  createCampaign:   (body)  => req("POST", "/tx/campaigns", body),
  getCampaign:      (id)    => req("GET",  `/tx/campaigns/${id}`),

  // export
  exportCampaign:   (id)    => download(`/tx/campaigns/${id}/export`),
  exportCampaignGroup: (id, platformId, platformName) => {
    const qs = platformId ? `?platform_id=${encodeURIComponent(platformId)}&platform_name=${encodeURIComponent(platformName || '')}` : '';
    return download(`/tx/campaign_groups/${id}/export${qs}`);
  },
  getCampaignGroupPlatforms: (id) => req("GET", `/tx/campaign_groups/${id}/platforms`),
  exportCampaigns:  (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return download(`/tx/campaigns/export${qs ? "?" + qs : ""}`);
  },

  // activity
  getActivity:      (params = {}) => {
    const qs = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString();
    return req("GET", `/tx/activity${qs ? "?" + qs : ""}`);
  },
};

// Moved to shared/http/download.js; re-exported so existing imports keep working.
export { triggerDownload } from "../../shared/http/download.js";
